import {
  AccountBox,
  Comment,
  Delete,
  Print,
  ReceiptLong,
  SafetyDivider,
  Settings,
  TableBar,
} from '@mui/icons-material';
import { IconButton, Typography, Menu as ButtonsMenu, MenuItem, ListItemIcon, Divider, Box } from '@mui/material';
import { format } from 'date-fns';
import { uniq } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { removeOrder } from '../../../api/order/removeOrder';
import { OrderItem, Order as OrderType, PromotionApplied } from '../../../apiTypes';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import BillPrintModal from '../BillPrintModal';
import ChangeWaiterModal from './ChangeWaiterModal';
import EditCommentModal from './EditCommentModal';
import KitchenTicketPrintModal from './KitchenTicketPrintModal';
import SelectClientModal from './SelectClientModal';
import SplitOrderModal from './SplitOrderModal';

type Props = {
  order: DbDocument<OrderType>;
  items: OrderItem[];
  canEdit?: boolean;
  promotionsApplied: PromotionApplied[];
};

const OrderSettings = ({ order, items, canEdit, promotionsApplied }: Props) => {
  const { lang } = useLocale();
  const { dishesMap, worker, promotions } = useCafe();
  const { cafeSlug, cafe } = useAdmin();
  const [splitOrder, setSplitOrder] = useState(false);
  const [selectClient, setSelectClient] = useState(false);
  const [changeWaiterOpen, setChangeWaiterOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBillOpen, setIsBillOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const [kitchensForPrint, setKitchensForPrint] = useState<string[]>([]);

  const settingsRef = useRef<HTMLDivElement>(null);

  const availableKitchensForPrint = useMemo(
    () =>
      uniq(
        items.filter((i) => !i.printed && dishesMap[i.dishId]?.kitchenId).map((i) => dishesMap[i.dishId]?.kitchenId)
      ) as string[],
    [dishesMap, items]
  );

  const handleDeleteOrder = useCallback(async () => {
    if (worker) {
      await removeOrder({ order, worker, cafeSlug });
    }
  }, [cafeSlug, order, worker]);

  return (
    <>
      <Box ref={settingsRef} sx={{ display: 'flex', gap: 1 }}>
        <IconButton onClick={() => setIsSettingsOpen(true)} disabled={!canEdit}>
          <Settings />
        </IconButton>

        {availableKitchensForPrint.length > 0 && (
          <IconButton onClick={() => setKitchensForPrint(availableKitchensForPrint)} disabled={!canEdit}>
            <Print />
          </IconButton>
        )}

        <ButtonsMenu
          anchorEl={settingsRef.current}
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography variant="body2" color={(t) => t.palette.text.secondary} sx={{ mx: 2, mb: 0.5 }}>
            {`${t.created[lang]}: ${format(order.created, 'dd.MM HH:mm')}`}
          </Typography>
          <Typography variant="body2" color={(t) => t.palette.text.secondary} sx={{ mx: 2, mb: 0.5 }}>
            {`${t.waiter[lang]}: ${order.workerName}`}
          </Typography>
          {order.clientName && (
            <Typography variant="body2" color={(t) => t.palette.text.secondary} sx={{ mx: 2, mb: 0.5 }}>
              {`${t.client[lang]}: ${order.clientName}`}
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />

          {worker?.credentials.select_client_in_order && (
            <MenuItem
              onClick={() => {
                setSelectClient(true);
                setIsSettingsOpen(false);
              }}
              disabled={order.paid !== null}
            >
              <ListItemIcon>
                <AccountBox />
              </ListItemIcon>
              {t.select_client[lang]}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setSplitOrder(true);
              setIsSettingsOpen(false);
            }}
            disabled={order.paid !== null || items.length < 2}
          >
            <ListItemIcon>
              <SafetyDivider />
            </ListItemIcon>
            {t.split_order[lang]}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setIsBillOpen(true);
              setIsSettingsOpen(false);
            }}
          >
            <ListItemIcon>
              <ReceiptLong />
            </ListItemIcon>
            {t.view_bill[lang]}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setChangeWaiterOpen(true);
              setIsSettingsOpen(false);
            }}
          >
            <ListItemIcon>
              <TableBar />
            </ListItemIcon>
            {t.сhange_waiter_or_table[lang]}
          </MenuItem>
          {!order.comment && (
            <MenuItem
              onClick={() => {
                setIsCommentOpen(true);
                setIsSettingsOpen(false);
              }}
            >
              <ListItemIcon>
                <Comment />
              </ListItemIcon>
              {t.add_comment[lang]}
            </MenuItem>
          )}
          {(items.length === 0 || order.status === 'ordered') && worker?.credentials.delete_items_in_order !== false && (
            <MenuItem
              onClick={() => {
                handleDeleteOrder();
                setIsSettingsOpen(false);
              }}
            >
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              {t.remove[lang]}
            </MenuItem>
          )}
        </ButtonsMenu>
      </Box>

      {splitOrder && worker && (
        <SplitOrderModal
          onClose={() => setSplitOrder(false)}
          order={order}
          cafe={cafe}
          dishesMap={dishesMap}
          workerName={worker.name}
          workerId={worker.id}
        />
      )}

      {changeWaiterOpen && <ChangeWaiterModal onClose={() => setChangeWaiterOpen(false)} order={order} />}

      {isBillOpen && (
        <BillPrintModal
          discount={order.paid ? order.paid.discount : order.discount || 0}
          service={order.paid ? order.paid.service : cafe?.bill?.defaultService || 0}
          onClose={() => setIsBillOpen(false)}
          order={order}
          promotionsApplied={promotionsApplied}
        />
      )}

      {kitchensForPrint.length > 0 && (
        <KitchenTicketPrintModal
          onClose={() => setKitchensForPrint((prev) => prev.slice(1))}
          order={order}
          kitchenId={kitchensForPrint[0]}
        />
      )}

      {isCommentOpen && <EditCommentModal onClose={() => setIsCommentOpen(false)} order={order} />}

      {selectClient && <SelectClientModal onClose={() => setSelectClient(false)} order={order} />}
    </>
  );
};

export default OrderSettings;
